import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next"; // I18N
import useApi from "../../hooks/useApi";
import { paymentMobileMoney, paymentPaystack } from "../../api/payment";
import { banks, paymentMethods } from "../../constants/banks";
import { getMaskedAccount } from "../../constants/dahabshiilAccounts";
import defaultImg from "../../images/default donate.jpg";
import BankStepsModal from "./BankStepsModal";
import MediaDisplay from "../MediaDisplay/MediaDisplay";
import "./DonationForm.css";

const DonationForm = ({ project, onDonationComplete }) => {
  const { t } = useTranslation(); // I18N
  // API hooks
  const {
    isLoading: mobileMoneyLoading,
    error: mobileMoneyError,
    data: mobileMoneyData,
    apiCall: callMobileMoneyApi,
    clearStates: clearMobileMoneyStates,
  } = useApi();

  const {
    isLoading: paystackLoading,
    error: paystackError,
    data: paystackData,
    apiCall: callPaystackApi,
    clearStates: clearPaystackStates,
  } = useApi();

  // State
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [selectedBank, setSelectedBank] = useState(null);
  const [supportedBanks, setSupportedBanks] = useState([]);
  const [redirectCount, setRedirectCount] = useState(5);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [showBankStepsModal, setShowBankStepsModal] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    name: "",
    phone: "252",
    firstName: "",
    lastName: "",
    email: "",
  });

  // Validation errors
  const [errors, setErrors] = useState({});

  // Quick amount buttons
  const quickAmounts = [5, 10, 50, 100, 500, 1000];

  // Helper function to extract error messages
  const getErrorMessage = (error) => {
    if (!error) return null;

    // API returns format: {"statusCode":400,"message":"This is not a valid zaad mobile number"}
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }

    if (error?.response?.data && typeof error.response.data === "string") {
      try {
        const parsed = JSON.parse(error.response.data);
        if (parsed.message) {
          return parsed.message;
        }
      } catch (e) {
        // Not JSON, just return the string
        return error.response.data;
      }
    }

    if (error?.message) {
      return error.message;
    }

    return t('donate.form.errors.generic');
  };

  // Initialize supported banks
  useEffect(() => {
    if (project?.Project_accounts) {
      const currentAccounts = project.Project_accounts;
      const updatedBanks = banks.map((bank) => {
        // More flexible matching function
        const matchingAccount = currentAccounts.find((target) => {
          const bankIdLower = target.bankId.toLowerCase().trim();
          const bankNameLower = bank.name.toLowerCase().trim();
          const bankDisplayLower = bank.display.toLowerCase().trim();

          return (
            bankIdLower === bankNameLower || bankIdLower === bankDisplayLower
          );
        });

        // Debug logging for Dahabshiil
        if (bank.name === "dahabshiil bank" && matchingAccount) {
          console.log("Project ID:", project.project_id);
          console.log(
            "getMaskedAccount result:",
            getMaskedAccount(project.project_id),
          );
        }

        return {
          ...bank,
          disable: !matchingAccount,
          accNo: matchingAccount ? matchingAccount.AccNo : null,
          maskedAccount:
            bank.name === "dahabshiil bank" && matchingAccount
              ? getMaskedAccount(project.project_id)
              : null,
        };
      });
      setSupportedBanks(updatedBanks);
    } else {
      // If no Project_accounts, enable all banks for testing
      setSupportedBanks(
        banks.map((bank) => ({
          ...bank,
          disable: false,
          accNo: `test-${bank.name}`,
        })),
      );
    }
  }, [project]);

  // Clear selected payment method if it becomes unavailable
  useEffect(() => {
    if (paymentMethod && supportedBanks.length > 0) {
      const isCurrentMethodAvailable = supportedBanks.some(
        (bank) => bank.method === paymentMethod && !bank.disable,
      );
      if (!isCurrentMethodAvailable) {
        setPaymentMethod("");
        setSelectedBank(null);
      }
    }
  }, [supportedBanks]);

  // Debug logging
  useEffect(() => {
    if (project?.Project_accounts) {
      console.log("Project accounts:", project.Project_accounts);
      console.log("All banks:", banks);
      console.log("Supported banks:", supportedBanks);

      // Debug matching
      project.Project_accounts.forEach((acc) => {
        const matchingBank = banks.find(
          (bank) => bank.name.toLowerCase() === acc.bankId.toLowerCase(),
        );
        console.log(
          `Account ${acc.bankId} -> Bank: ${
            matchingBank ? matchingBank.display : "NOT FOUND"
          }`,
        );
      });
    }
  }, [project, supportedBanks]);

  // Validation functions
  const validateAmount = (value) => {
    if (!value || value <= 0)
      return t('donate.form.errors.amountRequired');
    if (value > 10000) return t('donate.form.errors.amountMax');
    return null;
  };

  const validateName = (value) => {
    if (!value || value.length < 2) return t('donate.form.errors.nameMin');
    if (value.length > 50) return t('donate.form.errors.nameMax');
    return null;
  };

  const validatePhone = (value) => {
    if (!value) return t('donate.form.errors.phoneRequired');
    if (value.length !== 12) return t('donate.form.errors.phoneLength');

    // General Somalia number format for all mobile money services
    if (!/^252\d{9}$/.test(value)) {
      return t('donate.form.errors.phoneFormat');
    }

    return null;
  };

  // More lenient phone validation for real-time checking
  const isPhoneValid = (value) => {
    if (!value) return false;

    // General Somalia number format for all mobile money services (12 digits)
    return value.length === 12 && /^252\d{9}$/.test(value);
  };

  const validateEmail = (value) => {
    if (!value) return t('donate.form.errors.emailRequired');
    if (!/\S+@\S+\.\S+/.test(value)) return t('donate.form.errors.emailInvalid');
    return null;
  };

  // Handle amount selection
  const handleQuickAmount = (value) => {
    setAmount(value);
    setErrors({ ...errors, amount: null });
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);
    setErrors({ ...errors, amount: null });
  };

  // Handle payment method selection
  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);

    // Auto-select Paystack when card is chosen (like website2)
    if (method === "card") {
      const paystackBank = supportedBanks.find(
        (bank) => bank.method === "card",
      );
      if (paystackBank && !paystackBank.disable) {
        setSelectedBank(paystackBank);
      } else {
        setSelectedBank(null);
      }
    } else {
      setSelectedBank(null);
    }
  };

  // Handle bank selection
  const handleBankSelection = (bank) => {
    if (!bank.disable) {
      setSelectedBank(bank);
    }
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setErrors({ ...errors, [field]: null });
  };

  // Handle phone input with formatting
  const handlePhoneChange = (e) => {
    let value = e.target.value;
    if (!value.startsWith("252")) {
      value = "252";
    }
    const numericValue = value.replace(/\D/g, "");
    if (numericValue.length <= 12) {
      handleInputChange("phone", numericValue);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    const amountError = validateAmount(amount);
    if (amountError) newErrors.amount = amountError;

    if (paymentMethod === "mobile_money") {
      const nameError = validateName(formData.name);
      if (nameError) newErrors.name = nameError;

      const phoneError = validatePhone(formData.phone);
      if (phoneError) newErrors.phone = phoneError;
    }

    if (paymentMethod === "card") {
      const firstNameError = validateName(formData.firstName);
      if (firstNameError) newErrors.firstName = firstNameError;

      const lastNameError = validateName(formData.lastName);
      if (lastNameError) newErrors.lastName = lastNameError;

      const emailError = validateEmail(formData.email);
      if (emailError) newErrors.email = emailError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit handlers
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (paymentMethod === "mobile_money") {
        if (!selectedBank) return;
        await callMobileMoneyApi(
          paymentMobileMoney({
            name: formData.name,
            phone: formData.phone,
            amount: String(amount),
            project_id: Number(project.project_id || project.id),
            accNo: selectedBank.accNo,
            service: selectedBank.service,
            bank: selectedBank.name,
          }),
        );
      } else if (paymentMethod === "card") {
        // For card payments, use the auto-selected Paystack bank
        const paystackBank = supportedBanks.find(
          (bank) => bank.method === "card",
        );
        if (!paystackBank) return;

        const paystackData = {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          amount: String(amount),
          project_id: Number(project.project_id || project.id),
          accNo: paystackBank.accNo,
          service: "paystack", // Must be exactly "paystack"
          bank: "Paystack", // Must be exactly "Paystack"
        };

        console.log("🔍 PAYSTACK REQUEST DATA:", paystackData);
        console.log("🔍 PAYSTACK BANK:", paystackBank);

        await callPaystackApi(paymentPaystack(paystackData));
      }
    } catch (error) {
      console.error("Payment error:", error);
    }
  };

  // Handle successful payment responses
  useEffect(() => {
    if (paystackData) {
      setIsRedirecting(true);
      const timer = setTimeout(() => {
        window.location.href = paystackData.url;
        clearPaystackStates();
      }, 5000);

      const countdownTimer = setInterval(() => {
        setRedirectCount((prev) => prev - 1);
      }, 1000);

      return () => {
        clearTimeout(timer);
        clearInterval(countdownTimer);
      };
    }

    if (mobileMoneyData) {
      if (onDonationComplete) onDonationComplete({ type: "success" });
      setTimeout(() => {
        clearMobileMoneyStates();
      }, 3000);
    }
  }, [paystackData, mobileMoneyData]);

  // Auto-clear errors
  useEffect(() => {
    if (mobileMoneyError || paystackError) {
      // Enhanced error logging for debugging
      if (paystackError) {
        console.error("🔥 PAYSTACK ERROR DETAILS:");
        console.error("Full error:", paystackError);
        console.error("Response status:", paystackError?.response?.status);
        console.error("Response data:", paystackError?.response?.data);
        console.error("Error message:", paystackError?.message);
      }

      if (onDonationComplete) {
        const errorMessage =
          getErrorMessage(mobileMoneyError) || getErrorMessage(paystackError);
        onDonationComplete({ type: "error", message: errorMessage });
      }
      const timer = setTimeout(() => {
        clearMobileMoneyStates();
        clearPaystackStates();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [mobileMoneyError, paystackError]);

  // Get filtered banks for selected payment method
  const getFilteredBanks = () => {
    return supportedBanks.filter((bank) => bank.method === paymentMethod);
  };

  // Check if a payment method has any available banks
  const isPaymentMethodAvailable = (methodId) => {
    return supportedBanks.some(
      (bank) => bank.method === methodId && !bank.disable,
    );
  };

  const isFormValid = () => {
    // Basic requirements
    if (!amount || !paymentMethod) {
      return false;
    }

    // For card payments, check if Paystack is available (auto-selected)
    if (paymentMethod === "card") {
      const paystackBank = supportedBanks.find(
        (bank) => bank.method === "card",
      );
      if (!paystackBank || paystackBank.disable) {
        return false;
      }
    } else {
      // For other payment methods, require manual bank selection
      if (!selectedBank) {
        return false;
      }
    }

    // Payment method specific requirements
    if (paymentMethod === "mobile_money") {
      if (!formData.name || !formData.phone) {
        return false;
      }

      // Run live validation for mobile money fields
      const nameError = validateName(formData.name);
      const phoneValid = isPhoneValid(formData.phone);

      if (nameError || !phoneValid) {
        return false;
      }
    }

    if (paymentMethod === "card") {
      if (!formData.firstName || !formData.lastName || !formData.email) {
        return false;
      }

      // Run live validation for card fields
      const firstNameError = validateName(formData.firstName);
      const lastNameError = validateName(formData.lastName);
      const emailError = validateEmail(formData.email);

      if (firstNameError || lastNameError || emailError) {
        return false;
      }
    }

    // Validate amount
    const amountError = validateAmount(amount);
    if (amountError) {
      return false;
    }

    return true;
  };

  return (
    <div className="donation-form">
      {/* Project Summary */}
      <div className="project-summary">
        <MediaDisplay
          src={project.images?.url_1 || defaultImg}
          alt={project.title}
          className="project-image"
          showPlayButton={false}
        />
        <div className="project-info">
          <h4>{t('donate.form.supporting', { title: project.title })}</h4>
          <p>{t('donate.form.intro')}</p>
        </div>
      </div>

      {/* Success/Error Messages */}
      {mobileMoneyData && (
        <div className="notification success">
          <strong>{t('donate.form.successHeader')}</strong> {t('donate.form.successBody')}
        </div>
      )}

      {isRedirecting && paystackData && (
        <div className="countdown-notice">
          <h4>{t('donate.form.redirecting')}</h4>
          <p>{t('donate.form.redirectIn', { count: redirectCount })}</p>
          <div className="loading-spinner"></div>
        </div>
      )}

      {(mobileMoneyError || paystackError) && (
        <div className="notification error">
          <strong>{t('donate.form.paymentFailed')}</strong>{" "}
          {getErrorMessage(mobileMoneyError) || getErrorMessage(paystackError)}
        </div>
      )}

      {/* Amount Selection */}
      <div className="form-group">
        <label className="form-label">{t('donate.form.enterAmount')}</label>
        <div className="amount-buttons row">
          {quickAmounts.map((quickAmount) => (
            <div key={quickAmount} className="col-md-4 col-6 mb-2">
              <button
                type="button"
                className={`btn w-100 ${
                  amount == quickAmount ? "selected" : ""
                }`}
                onClick={() => handleQuickAmount(quickAmount)}
              >
                ${quickAmount}
              </button>
            </div>
          ))}
        </div>

        <div
          className={`input-container mt-3 ${
            errors.amount ? "amount-error" : ""
          }`}
        >
          <span className="currency-symbol">$</span>
          <input
            type="number"
            className="amount-input"
            placeholder={t('donate.form.customAmount')}
            value={amount}
            onChange={handleAmountChange}
          />
        </div>
        {errors.amount && <div className="error-message">{errors.amount}</div>}
      </div>

      {/* Payment Method Selection */}
      <div className="form-group">
        <label className="form-label">{t('donate.form.selectMethod')}</label>
        {paymentMethods.map((method) => {
          const isAvailable = isPaymentMethodAvailable(method.id);
          return (
            <div
              key={method.id}
              className={`payment-option ${
                paymentMethod === method.id ? "selected" : ""
              } ${!isAvailable ? "disabled" : ""}`}
              onClick={() =>
                isAvailable && handlePaymentMethodChange(method.id)
              }
              title={
                !isAvailable
                  ? t('donate.form.methodUnavailable')
                  : ""
              }
            >
              <input
                type="radio"
                name="paymentMethod"
                value={method.id}
                checked={paymentMethod === method.id}
                disabled={!isAvailable}
                onChange={() => {}}
              />
              <label>
                <div>
                  <strong>{method.name}</strong>
                  <br />
                  <small>{method.description}</small>
                </div>
              </label>
            </div>
          );
        })}
      </div>

      {/* Bank/Service Selection - Hidden for card payments (auto-selected) */}
      {paymentMethod && paymentMethod !== "card" && (
        <div className="form-group">
          <label className="form-label">
            {paymentMethod === "mobile_money"
              ? t('donate.form.chooseMobileService')
              : t('donate.form.chooseBank')}
          </label>
          <div className="row">
            {getFilteredBanks().map((bank) => (
              <div key={bank.id} className="col-md-6 col-12 mb-2">
                <div
                  className={`bank-option ${
                    selectedBank?.id === bank.id ? "selected" : ""
                  } ${bank.disable ? "disabled" : ""}`}
                  onClick={() => handleBankSelection(bank)}
                  title={
                    bank.disable
                      ? t('donate.form.methodUnavailable')
                      : bank.display || bank.name
                  }
                >
                  <img
                    src={bank.img}
                    alt={bank.display || bank.name}
                    style={{ maxHeight: "40px", width: "auto" }}
                  />
                </div>
              </div>
            ))}
          </div>
          {getFilteredBanks().some((bank) => bank.disable) && (
            <p className="text-muted mt-2" style={{ fontSize: "0.9rem" }}>
              <em>{t('donate.form.grayedOut')}</em>
            </p>
          )}
        </div>
      )}

      {/* Bank Steps Guide */}
      {selectedBank && paymentMethod === "bank" && (
        <div className="form-group">
          <div className="bank-steps-guide">
            <h6>{t('donate.form.needHelp')}</h6>
            <p>
              {t('donate.form.helpBody', { bank: selectedBank.display })}
            </p>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => setShowBankStepsModal(true)}
            >
              {t('donate.form.showSteps')}
            </button>
          </div>
        </div>
      )}

      {/* Payment Details Forms */}
      {selectedBank && paymentMethod === "mobile_money" && (
        <div className="form-group">
          <label className="form-label">{t('donate.form.yourInfo')}</label>

          <input
            type="text"
            className="form-control mb-3"
            placeholder={t('donate.form.fullName')}
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
          />
          {errors.name && <div className="error-message">{errors.name}</div>}

          <input
            type="text"
            className="form-control"
            placeholder={
              selectedBank?.name === "e-dahab"
                ? t('donate.form.edahabPhonePlaceholder')
                : t('donate.form.phonePlaceholder')
            }
            value={formData.phone}
            onChange={handlePhoneChange}
          />
          {errors.phone && <div className="error-message">{errors.phone}</div>}
          {/* {selectedBank?.name === "e-dahab" && (
            <small className="text-muted">
              E-Dahab phone numbers should start with 25265, 25266, 25262, or
              25264
            </small>
          )} */}
        </div>
      )}

      {paymentMethod === "card" && (
        <div className="form-group">
          <div
            className="paystack-notice mb-3"
            style={{
              backgroundColor: "#f8f9fa",
              border: "1px solid #dee2e6",
              borderRadius: "8px",
              padding: "1rem",
            }}
          >
            <p
              className="mb-2"
              style={{ color: "#504747", fontSize: "0.95rem" }}
            >
              <strong>{t('donate.form.paystackNote')}</strong> {t('donate.form.paystackBody')}
            </p>
            <p
              className="mb-0"
              style={{ color: "#504747", fontSize: "0.9rem" }}
            >
              {t('donate.form.paystackStatement')}{" "}
              <strong>"Shaqodoon International - Kenya"</strong>. {t('donate.form.paystackFeeNote')}{" "}
              <span style={{ color: "red" }}>3.8%</span> {t('donate.form.toEachPayment')}
            </p>
          </div>

          <label className="form-label">{t('donate.form.cardHolderInfo')}</label>

          <div className="row">
            <div className="col-md-6">
              <input
                type="text"
                className="form-control mb-3"
                placeholder={t('donate.form.firstName')}
                value={formData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
              />
              {errors.firstName && (
                <div className="error-message">{errors.firstName}</div>
              )}
            </div>
            <div className="col-md-6">
              <input
                type="text"
                className="form-control mb-3"
                placeholder={t('donate.form.lastName')}
                value={formData.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
              />
              {errors.lastName && (
                <div className="error-message">{errors.lastName}</div>
              )}
            </div>
          </div>

          <input
            type="email"
            className="form-control"
            placeholder={t('donate.form.emailAddress')}
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
          />
          {errors.email && <div className="error-message">{errors.email}</div>}
        </div>
      )}

      {/* Submit Button */}
      <button
        type="button"
        className="btn-primary"
        disabled={!isFormValid() || mobileMoneyLoading || paystackLoading}
        onClick={handleSubmit}
      >
        {mobileMoneyLoading || paystackLoading ? (
          <>
            <span className="loading-spinner"></span>
            {t('donate.form.processing')}
          </>
        ) : (
          t('donate.form.donateAmount', { amount: amount || "0" })
        )}
      </button>

      {/* Bank Steps Modal */}
      <BankStepsModal
        show={showBankStepsModal}
        onHide={() => setShowBankStepsModal(false)}
        selectedBank={selectedBank}
        amount={amount}
      />
    </div>
  );
};

export default DonationForm;
